import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CIRCLES_CANDIDATES_FETCH,
  CANDIDATES_DETAILS_FETCH,
  COMPANY_INFO_FETCH,
} from './constants';
import { PAGINATION } from '@/constants/pagination';

const getCandidateDetails = async ({ id }, { extra: { http } }) => {
  try {
    const circleOptions = await http.get(`/contacts/${id}`);
    return circleOptions;
  } catch {}
};

const getCirclesCandidates = async (
  {
    companyId,
    filters,
    page,
    sortOption = {},
    ignoreId = 0,
    userIdsArray = [],
  },
  { extra: { http } }
) => {
  try {
    const { location, tags, skills, jobTitle, pastCompanySelected } = filters;
    const { limit, offset } = page;
    const { element, order } = sortOption;

    const params = {
      limit: limit || PAGINATION.CONTACTS,
      offset: offset || 0,
      sort_element: element || 'name',
      sort_order: order || 'asc',
      ignore_user_ids: ignoreId === 0 ? [] : [ignoreId],
      extended_fields: true,
    };

    if (tags?.length) {
      const tagIds = tags.map((tag) => tag?.id);
      params.tags = tagIds;
    }

    if (skills?.length) {
      const skillIds = skills.map((skill) => skill?.id);
      params.skills = skillIds;
    }

    if (jobTitle?.length) {
      const jobTitleLabel = jobTitle.map((job) => job?.label);
      params.position = jobTitleLabel;
    }

    if (location.length) {
      const locationIds = location.map(({ value }) => value);
      params.location = locationIds;
    }

    if (userIdsArray?.length) {
      params.user_ids =
        userIdsArray[0] === 'org' ? [] : userIdsArray?.map((id) => +id);
    } else {
      params.user_ids = [];
    }

    if (pastCompanySelected) {
      params.past_company_id = [companyId];
    } else {
      params.company_id = companyId;
    }

    const circleOptions = await http.post(`/search/circles/contacts`, params);
    return circleOptions;
  } catch {}
};

const getCompanyInfo = async ({ companyId }, { extra: { http } }) => {
  try {
    const companyInfo = await http.get(`/companies/${companyId}`);
    return companyInfo;
  } catch {}
};

const getActivities = async ({ contactId }, { extra: { http } }) => {
  try {
    const activities = await http.get(`/contacts/${contactId}/activities`);
    return activities;
  } catch {}
};

const getNotes = async ({ contactId }, { extra: { http } }) => {
  try {
    const notes = await http.get(`/contacts/${contactId}/notes`, {
      params: {
        items: 50,
      },
    });
    return notes;
  } catch {}
};

const addNotes = async (
  { contactId, body, isPrivate = false },
  { extra: { http } }
) => {
  try {
    const notes = await http.post(`/contacts/${contactId}/notes`, {
      body,
      private: isPrivate,
    });
    return notes;
  } catch {}
};

const updateNotes = async (
  { contactId, body, noteId, isPrivate = false },
  { extra: { http } }
) => {
  try {
    const notes = await http.put(`/contacts/${contactId}/edit_note`, {
      note_id: noteId,
      body,
      private: isPrivate,
    });
    return notes;
  } catch {}
};

const addTags = async ({ contactId, tags }, { extra: { http } }) => {
  try {
    const result = await http.post(`/contacts/${contactId}/tags_add`, {
      tags,
    });
    return { contactId, data: result.data };
  } catch {}
};

const removeTag = async ({ contactId, tags }, { extra: { http } }) => {
  try {
    const result = await http.post(`/contacts/${contactId}/tags_remove`, {
      tags,
    });
    return { contactId, data: result.data };
  } catch {}
};

export const fetchCirclesCandidates = createAsyncThunk(
  CIRCLES_CANDIDATES_FETCH,
  getCirclesCandidates
);

export const fetchCandidateDetails = createAsyncThunk(
  CANDIDATES_DETAILS_FETCH,
  getCandidateDetails
);

export const fetchCompanyInfo = createAsyncThunk(
  COMPANY_INFO_FETCH,
  getCompanyInfo
);

export const fetchCandidateActivities = createAsyncThunk(
  'candidates/activities/fetch',
  getActivities
);

export const fetchCandidateNotes = createAsyncThunk(
  'candidates/notes/fetch',
  getNotes
);

export const addCandidateNotes = createAsyncThunk(
  'candidates/notes/add',
  addNotes
);

export const updateCandidateNotes = createAsyncThunk(
  'candidates/notes/update',
  updateNotes
);

export const addCandidateTags = createAsyncThunk(
  'candidates/tags/add',
  addTags
);

export const removeCandidateTags = createAsyncThunk(
  'candidates/tags/remove',
  removeTag
);
